/**
 * offlinePrefetch — proaktiv nedlasting av data for offline-bruk.
 *
 * Kaller dei viktigaste GET-endpoints medan brukaren er online, slik at
 * Axios-response-interceptoren (i api.js) cachar dei automatisk i IndexedDB.
 * Sluttresultat: dersom brukaren mister nettet etterpå, kan pantry og
 * handleliste framleis visast.
 */
import { api, getWeekStart } from "@/lib/api";
import { get as idbGet, set as idbSet, createStore } from "idb-keyval";

const META_STORE = createStore("pb-offline-cache", "cache");
const META_KEY = "__prefetch_meta__";

export async function getPrefetchMeta() {
  try {
    return (await idbGet(META_KEY, META_STORE)) || null;
  } catch {
    return null;
  }
}

async function setPrefetchMeta(meta) {
  try {
    await idbSet(META_KEY, meta, META_STORE);
  } catch { /* ignore */ }
}

/**
 * Køyrer full prefetch. `onProgress({step, total, label})` kallast for kvart
 * steg. Returnerer { ok: bool, at: iso, errors: [{step, error}] }.
 */
export async function prefetchOfflineData({ shift = "1", onProgress } = {}) {
  const weekStart = getWeekStart();
  const steps = [
    { key: "boat", label: "Aktiv båt", run: () => api.getActiveBoat() },
    { key: "trip", label: "Aktiv tur", run: () => api.getActiveTrip() },
    { key: "locations", label: "Lagringslokasjonar", run: () => api.getStorageLocations() },
    { key: "pantry", label: "Beholdning", run: () => api.getPantry() },
    { key: "verification", label: "Opptelling-status", run: () => api.getPantryVerificationStatus() },
    { key: "withdrawals", label: "Uttak", run: () => api.getWithdrawals() },
    { key: "shopping-lists", label: "Handlelister", run: () => api.listAllShoppingLists() },
    { key: "shopping-main", label: `Hovedhandleliste (uke ${weekStart})`, run: () => api.getShoppingList(weekStart, "", shift) },
  ];
  const total = steps.length;
  const errors = [];
  for (let i = 0; i < steps.length; i++) {
    const s = steps[i];
    if (typeof onProgress === "function") {
      try { onProgress({ step: i + 1, total, label: s.label }); } catch { /* ignore */ }
    }
    try {
      await s.run();
    } catch (e) {
      errors.push({ step: s.key, error: String(e?.message || e) });
    }
  }
  const meta = { at: new Date().toISOString(), errors, weekStart, shift };
  await setPrefetchMeta(meta);
  window.dispatchEvent(new CustomEvent("pb-prefetch-done", { detail: meta }));
  return { ok: errors.length === 0, ...meta };
}
